import React, { useEffect, useRef, useState } from "react";

import {
  ArrowLeft,
  Folder,
  FileImage,
  Film,
  Music,
  FileText,
  Globe,
  LayoutGrid,
  Check,
  Search,
  Home,
  Loader2,
  Play,
  Plus,
  ChevronRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { OptisignsAssetItem } from "@/lib/agent/payload-parser";

import {
  useOptisignsAssets,
  type OptisignsAssetRefSelection,
} from "./use-optisigns-assets";

const FILE_TYPE_FILTERS: { label: string; value?: string }[] = [
  { label: "All", value: undefined },
  { label: "Images", value: "image" },
  { label: "Videos", value: "video" },
  { label: "Audio", value: "audio" },
  { label: "Docs", value: "document" },
  { label: "Web", value: "web" },
];

/** `m:ss` for a positive duration in seconds, else null (no pill). */
function formatDuration(seconds?: number): string | null {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Lucide fallback icon for an asset that has no usable thumbnail. */
function assetFallbackIcon(asset: OptisignsAssetItem): React.ReactElement {
  if (asset.type === "file") {
    switch (asset.fileType) {
      case "image":
        return <FileImage className="w-6 h-6" />;
      case "video":
        return <Film className="w-6 h-6" />;
      case "audio":
        return <Music className="w-6 h-6" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  }
  if (asset.type === "web") return <Globe className="w-6 h-6" />;
  return <LayoutGrid className="w-6 h-6" />;
}

/**
 * Thumbnail filling the (square) tile, falling back to a type icon when there's
 * no thumbnail or it 404s. Documents are portrait pages and app icons are small,
 * so both are `contain`; photos/posters `cover`.
 */
function AssetThumb({ asset }: { asset: OptisignsAssetItem }): React.ReactElement {
  const [errored, setErrored] = useState(false);
  if (asset.thumbnailUrl && !errored) {
    return (
      <img
        src={asset.thumbnailUrl}
        alt={asset.name}
        className={cn(
          asset.thumbnailIsAppIcon
            ? "h-1/2 w-1/2 object-contain"
            : asset.fileType === "document"
              ? "w-full h-full object-contain bg-white"
              : "w-full h-full object-cover",
        )}
        loading="lazy"
        onError={() => setErrored(true)}
      />
    );
  }
  return (
    <span className="text-muted-foreground">{assetFallbackIcon(asset)}</span>
  );
}

/** Short corner badge (top-left) for non-obvious types — null for plain images. */
function cornerBadge(asset: OptisignsAssetItem): string | null {
  if (asset.type === "file" && asset.fileType === "document")
    return asset.numPages ? `PDF · ${asset.numPages}p` : "PDF";
  if (asset.type === "web") return "Web";
  if (asset.type === "app") return "App";
  return null;
}

/** Selectable, type-aware asset tile. */
function AssetTile({
  asset,
  selected,
  onToggle,
}: {
  asset: OptisignsAssetItem;
  selected: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const duration = formatDuration(asset.duration);
  const isVideo = asset.type === "file" && asset.fileType === "video";
  const badge = cornerBadge(asset);

  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "group relative aspect-square rounded-xl overflow-hidden border bg-muted text-left transition-all",
        selected
          ? "border-primary ring-2 ring-primary"
          : "border-border hover:border-primary/50 hover:-translate-y-0.5",
      )}
    >
      {/* Thumbnail / type-icon fallback */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        <AssetThumb asset={asset} />
      </div>

      {/* Video: play affordance */}
      {isVideo && (
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white transition-transform group-hover:scale-110">
          <Play className="w-4 h-4 fill-current" />
        </span>
      )}

      {/* Corner type badge (docs / web / app) */}
      {badge && (
        <span className="absolute top-1.5 left-1.5 z-10 text-[9px] font-bold uppercase tracking-wide text-white bg-black/50 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
          {badge}
        </span>
      )}

      {/* Duration (video / audio) */}
      {duration && (
        <span className="absolute bottom-1.5 right-1.5 z-10 text-[9px] font-bold text-white bg-black/50 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
          {duration}
        </span>
      )}

      {/* Selection check — visible on hover, filled when selected */}
      <span
        className={cn(
          "absolute top-1.5 right-1.5 z-10 w-5 h-5 rounded-full flex items-center justify-center border transition-opacity",
          selected
            ? "bg-primary border-primary text-primary-foreground opacity-100"
            : "bg-black/30 border-white/70 text-white opacity-0 group-hover:opacity-100",
        )}
      >
        {selected && <Check className="w-3 h-3" />}
      </span>

      {/* Name on a scrim */}
      <div className="absolute inset-x-0 bottom-0 z-[5] px-2 pt-4 pb-1.5 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-[11px] font-semibold text-white truncate drop-shadow">
          {asset.name}
        </p>
      </div>
    </button>
  );
}

interface OptisignsAssetBrowserProps {
  projectId?: string;
  /** "card" = inline chat HITL (lean); "panel" = full "+" side panel (search + filters). */
  variant: "card" | "panel";
  /** Folder to open on mount (card seeds this from the agent's result; panel starts at root). */
  initialPath?: string | null;
  /** Agent's summary line — shown above the grid in the card variant. */
  summary?: string;
  /** Hand the picked assets to the composer as @asset chips. */
  onAddToMessage: (refs: OptisignsAssetRefSelection[]) => void;
  /** Panel-only: back to the chat. */
  onBack?: () => void;
}

/**
 * Live OptiSigns asset browser shared by the inline chat HITL card and the "+"
 * side panel. Browses folders + assets directly via {@link useOptisignsAssets}
 * (no agent turn), multi-selects, and on "Add to message" hands reference-only
 * picks to the composer as @asset chips — the agent then downloads media /
 * captures docs per the optisigns-assets skill when the user sends.
 */
export function OptisignsAssetBrowser({
  projectId,
  variant,
  initialPath,
  summary,
  onAddToMessage,
  onBack,
}: OptisignsAssetBrowserProps): React.ReactElement {
  const [path, setPath] = useState<string | null>(initialPath ?? null);
  const [search, setSearch] = useState("");
  const [fileType, setFileType] = useState<string | undefined>(undefined);
  const [justAdded, setJustAdded] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const {
    folders,
    assets,
    loading,
    loadingMore,
    error,
    selected,
    load,
    loadMore,
    prefetch,
    toggle,
    clearSelection,
    selectedRefs,
  } = useOptisignsAssets(projectId);

  const isPanel = variant === "panel";

  // Fetch on navigate/filter/search. Debounce search; everything else immediate.
  useEffect(() => {
    const delay = search ? 350 : 0;
    const timer = setTimeout(() => load({ path, search, fileType }), delay);
    return () => clearTimeout(timer);
  }, [path, search, fileType, load]);

  // Infinite scroll: refetch the next asset page when the sentinel nears the
  // bottom of the scroll container (loadMore no-ops when there's nothing more).
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadMore();
      },
      { root, rootMargin: "300px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, assets.length]);

  const openFolder = (folderPath: string): void => {
    clearSelection();
    setSearch("");
    setPath(folderPath);
  };

  const goRoot = (): void => {
    clearSelection();
    setSearch("");
    setPath(null);
  };

  const handleAdd = (): void => {
    if (selectedRefs.length === 0) return;
    onAddToMessage(selectedRefs);
    clearSelection();
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1600);
  };

  // Breadcrumb — every ancestor clickable. Shared by both variants.
  const breadcrumb = (
    <div className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap min-w-0">
      <button
        type="button"
        onClick={goRoot}
        className={cn(
          "inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:text-foreground hover:bg-muted",
          !path && "text-foreground font-medium",
        )}
      >
        <Home className="w-3 h-3" /> All assets
      </button>
      {path
        ?.replace(/^\//, "")
        .split("/")
        .map((segment, index, segments) => {
          const isLast = index === segments.length - 1;
          const target = "/" + segments.slice(0, index + 1).join("/");
          return (
            <React.Fragment key={target}>
              <span className="text-muted-foreground/50">/</span>
              {isLast ? (
                <span className="truncate max-w-[12rem] text-foreground font-medium px-1.5 py-0.5">
                  {segment}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => openFolder(target)}
                  className="truncate max-w-[12rem] rounded px-1.5 py-0.5 hover:text-foreground hover:bg-muted"
                >
                  {segment}
                </button>
              )}
            </React.Fragment>
          );
        })}
    </div>
  );

  const body = (
    <>
      {loading && (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && error && (
        <div className="px-3 py-6 text-center text-sm text-muted-foreground">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Folders — labeled section, click opens in place */}
          {folders.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                Folders
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                {folders.map((folder) => (
                  <button
                    key={folder.folderId}
                    type="button"
                    onClick={() => openFolder(folder.path ?? folder.name)}
                    onMouseEnter={() =>
                      void prefetch({
                        path: folder.path ?? folder.name,
                        search: "",
                        fileType,
                      })
                    }
                    className="group relative aspect-square rounded-xl border border-dashed border-border bg-muted/40 hover:bg-muted hover:border-primary/40 transition-colors flex flex-col items-center justify-center gap-1.5 p-2 text-center"
                  >
                    <ChevronRight className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-muted-foreground/60" />
                    <Folder className="w-7 h-7 text-primary/80" />
                    <span className="text-[11px] font-medium truncate max-w-full text-foreground">
                      {folder.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Assets */}
          {assets.length > 0 && (
            <div className="space-y-2">
              {folders.length > 0 && (
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                  Assets
                </p>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                {assets.map((asset) => (
                  <AssetTile
                    key={asset.assetId}
                    asset={asset}
                    selected={selected.has(asset.assetId)}
                    onToggle={() => toggle(asset.assetId)}
                  />
                ))}
              </div>
            </div>
          )}

          {assets.length === 0 && folders.length === 0 && (
            <div className="px-3 py-10 text-center text-sm text-muted-foreground">
              No OptiSigns assets found here.
            </div>
          )}

          {/* Infinite-scroll sentinel + spinner (assets only) */}
          {assets.length > 0 && (
            <>
              <div ref={sentinelRef} className="h-px w-full" />
              {loadingMore && (
                <div className="flex items-center justify-center py-4 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );

  const footer = (
    <div className="shrink-0 border-t border-border px-3 py-2.5 flex items-center justify-end gap-3">
      {selected.size > 0 && (
        <button
          type="button"
          onClick={clearSelection}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Clear
        </button>
      )}
      <Button
        size="sm"
        onClick={handleAdd}
        disabled={selected.size === 0 || justAdded}
        className="h-8 px-3 text-xs gap-1.5"
      >
        {justAdded ? (
          <>
            <Check className="w-3.5 h-3.5" /> Added
          </>
        ) : (
          <>
            <Plus className="w-3.5 h-3.5" />
            Add{selected.size > 0 ? ` ${selected.size}` : ""} to message
          </>
        )}
      </Button>
    </div>
  );

  // ---- Panel: full-height sidebar with search + filters ----
  if (isPanel) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="shrink-0 border-b border-border p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={onBack}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <img
              src="/optisigns-logo.svg"
              alt=""
              className="w-4 h-4 rounded-[3px]"
            />
            <span className="text-sm font-medium">OptiSigns Assets</span>
          </div>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search assets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-transparent border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="flex flex-wrap gap-1">
            {FILE_TYPE_FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                onClick={() => setFileType(f.value)}
                className={cn(
                  "text-xs px-2 py-1 rounded-md border transition-colors",
                  fileType === f.value
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-accent/50",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {path && breadcrumb}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
          {body}
        </div>

        {footer}
      </div>
    );
  }

  // ---- Card: lean inline chat HITL ----
  return (
    <div className="w-full min-w-0 max-w-[calc(100vw-3rem)] sm:max-w-2xl bg-card border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 p-3 border-b border-border">
        <img
          src="/optisigns-logo.svg"
          alt=""
          className="w-4 h-4 rounded-[3px] shrink-0"
        />
        {path ? breadcrumb : <span className="text-sm font-medium">OptiSigns assets</span>}
      </div>

      {summary && (
        <p className="px-3 pt-2.5 text-xs text-muted-foreground">{summary}</p>
      )}

      <div ref={scrollRef} className="max-h-72 sm:max-h-96 overflow-y-auto p-3 space-y-3">
        {body}
      </div>

      {footer}
    </div>
  );
}
